import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { UsersThree, UserPlus, PencilSimple, Prohibit, CheckCircle, ShieldCheck, UploadSimple, Eye, Key } from "@phosphor-icons/react";
import { toast } from "sonner";
import EmptyState from "@/components/EmptyState";

const PERMISSION_LABELS = {
    view_only: { label: "View Only", desc: "Can view ledger & reports. No edits.", icon: Eye,          tone: "border-slate-200 bg-slate-50 text-slate-700" },
    upload_only: { label: "Upload Only", desc: "Can only upload slips.", icon: UploadSimple,           tone: "border-blue-200 bg-blue-50 text-blue-800" },
    // v1.27.91 — DEO: view + upload + verify slips + add transactions. No delete,
    // no user mgmt, no Administration panel, no User Activity Log.
    data_entry_operator: { label: "Data Entry Operator", desc: "View all, upload + verify slips, add Cash/Payment txns. No delete, no admin panel.", icon: Key, tone: "border-amber-200 bg-amber-50 text-amber-800" },
    full_access: { label: "Full Access", desc: "All ops except delete + user management.", icon: ShieldCheck, tone: "border-emerald-200 bg-emerald-50 text-emerald-800" },
};

/**
 * v1.27.73 — Company Admin manages Users (staff sub-users) inside their own
 * company. Super User sees a read-only list across every company but
 * cannot create/edit — they must sign in as the Company Admin to make
 * changes (backend enforces this too).
 *
 * Staff sub-users:
 *   • view_only   → read-only (all GET requests)
 *   • upload_only → GETs + POST /api/uploads only
 *   • full_access → everything except DELETE + user mgmt
 *
 * ALL staff (any permission) are blocked from every DELETE endpoint by
 * the backend, so hiding delete buttons in the UI for staff is defence
 * in depth — the destructive action is impossible either way.
 */
export default function UsersMgmt() {
    const { user: me } = useAuth();
    const isSuper = !!me?.is_super_user;
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [createOpen, setCreateOpen] = useState(false);
    const [editing, setEditing] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.get("/admin/staff");
            setRows(r.data || []);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to load users");
        } finally {
            setLoading(false);
        }
    }, []);
    useEffect(() => { load(); }, [load]);

    const setActive = async (u, active) => {
        try {
            await api.post(`/admin/staff/${u.id}/${active ? "activate" : "deactivate"}`);
            toast.success(active ? "User activated" : "User deactivated");
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed");
        }
    };

    return (
        <div className="space-y-5" data-testid="users-mgmt-page">
            <div className="flex flex-wrap items-end justify-between gap-3">
                <div className="flex items-center gap-3">
                    <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-violet-100 text-violet-700">
                        <UsersThree size={22} weight="bold" />
                    </div>
                    <div>
                        <div className="text-xs uppercase tracking-[0.2em] text-slate-500">Administration</div>
                        <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900">Users</h1>
                        <p className="mt-0.5 text-sm text-slate-500">
                            {isSuper
                                ? "Read-only view of every company's staff sub-users. Sign in as the Company Admin to make changes."
                                : "Create and manage back-office users inside your company. Staff cannot delete records regardless of role."}
                        </p>
                    </div>
                </div>
                {!isSuper && (
                    <Button
                        data-testid="users-add-btn"
                        onClick={() => setCreateOpen(true)}
                        className="bg-violet-600 text-white hover:bg-violet-700"
                    >
                        <UserPlus size={16} className="mr-2" /> Add User
                    </Button>
                )}
            </div>

            {loading ? (
                <div className="rounded-xl border border-slate-200 bg-white p-8 text-center text-sm text-slate-500">Loading users…</div>
            ) : rows.length === 0 ? (
                <EmptyState
                    icon={UsersThree}
                    title="No staff users yet"
                    description={isSuper ? "No company has created a staff sub-user." : "Add your first user to give a teammate limited access to the portal."}
                    action={!isSuper && (
                        <Button className="mt-2 bg-violet-600 text-white hover:bg-violet-700" onClick={() => setCreateOpen(true)}>
                            <UserPlus size={16} className="mr-2" /> Add User
                        </Button>
                    )}
                />
            ) : (
                <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
                            <tr>
                                <th className="px-4 py-3 font-medium">Name</th>
                                <th className="px-4 py-3 font-medium">Email</th>
                                <th className="px-4 py-3 font-medium">Role</th>
                                {isSuper && <th className="px-4 py-3 font-medium">Company</th>}
                                <th className="px-4 py-3 font-medium">Status</th>
                                {!isSuper && <th className="px-4 py-3 text-right font-medium">Actions</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((u) => {
                                const perm = PERMISSION_LABELS[u.staff_permission] || { label: u.staff_permission || "—", tone: "bg-slate-100 text-slate-600", icon: Eye };
                                const Icon = perm.icon;
                                const isActive = u.active !== false;
                                return (
                                    <tr key={u.id} className="border-t border-slate-100" data-testid={`user-row-${u.id}`}>
                                        <td className="px-4 py-3 font-medium text-slate-800">{u.name}</td>
                                        <td className="px-4 py-3 font-mono text-xs text-slate-600">{u.email}</td>
                                        <td className="px-4 py-3">
                                            <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold ${perm.tone}`} data-testid={`user-perm-${u.id}`}>
                                                <Icon size={11} weight="fill" /> {perm.label}
                                            </span>
                                        </td>
                                        {isSuper && <td className="px-4 py-3 text-xs text-slate-600">{u.company_name || u.company_id || "—"}</td>}
                                        <td className="px-4 py-3">
                                            {isActive ? (
                                                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold text-emerald-800">
                                                    <CheckCircle size={11} weight="fill" /> ACTIVE
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center gap-1 rounded-full bg-slate-200 px-2 py-0.5 text-[10px] font-bold text-slate-700">
                                                    <Prohibit size={11} weight="fill" /> DEACTIVATED
                                                </span>
                                            )}
                                        </td>
                                        {!isSuper && (
                                            <td className="px-4 py-3 text-right">
                                                <div className="flex justify-end gap-1">
                                                    <Button variant="outline" size="sm" data-testid={`user-edit-${u.id}`} onClick={() => setEditing(u)}>
                                                        <PencilSimple size={14} className="mr-1" /> Edit
                                                    </Button>
                                                    {isActive ? (
                                                        <Button variant="outline" size="sm" data-testid={`user-deactivate-${u.id}`} className="border-rose-200 text-rose-700 hover:bg-rose-50" onClick={() => setActive(u, false)}>
                                                            <Prohibit size={14} className="mr-1" /> Deactivate
                                                        </Button>
                                                    ) : (
                                                        <Button variant="outline" size="sm" data-testid={`user-activate-${u.id}`} className="border-emerald-200 text-emerald-700 hover:bg-emerald-50" onClick={() => setActive(u, true)}>
                                                            <CheckCircle size={14} className="mr-1" /> Activate
                                                        </Button>
                                                    )}
                                                </div>
                                            </td>
                                        )}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            <UserDialog
                open={createOpen}
                onClose={() => setCreateOpen(false)}
                onSaved={() => { setCreateOpen(false); load(); }}
            />
            {editing && (
                <UserDialog
                    open
                    editing={editing}
                    onClose={() => setEditing(null)}
                    onSaved={() => { setEditing(null); load(); }}
                />
            )}
        </div>
    );
}

function UserDialog({ open, onClose, onSaved, editing }) {
    const [name, setName] = useState(editing?.name || "");
    const [email, setEmail] = useState(editing?.email || "");
    const [phone, setPhone] = useState(editing?.phone || "");
    const [password, setPassword] = useState("");
    const [perm, setPerm] = useState(editing?.staff_permission || "view_only");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (editing) {
            setName(editing.name || "");
            setEmail(editing.email || "");
            setPhone(editing.phone || "");
            setPerm(editing.staff_permission || "view_only");
            setPassword("");
        } else {
            setName(""); setEmail(""); setPhone(""); setPassword(""); setPerm("view_only");
        }
    }, [editing, open]);

    const save = async () => {
        setSaving(true);
        try {
            const cleanEmail = email.trim().toLowerCase();
            const cleanPhone = phone.replace(/\D/g, "").slice(-10);
            if (!editing && !cleanEmail && !cleanPhone) {
                toast.error("Provide an email or a 10-digit mobile number");
                setSaving(false); return;
            }
            if (cleanPhone && !/^[6-9]\d{9}$/.test(cleanPhone)) {
                toast.error("Mobile must be a 10-digit Indian number starting with 6-9");
                setSaving(false); return;
            }
            if (editing) {
                const body = { name, staff_permission: perm, email: cleanEmail, phone: cleanPhone };
                if (password) body.password = password;
                await api.patch(`/admin/staff/${editing.id}`, body);
                toast.success("User updated");
            } else {
                if (!name.trim() || !password.trim()) {
                    toast.error("Name and password are required");
                    setSaving(false); return;
                }
                await api.post("/admin/staff", { name, email: cleanEmail, phone: cleanPhone, password, staff_permission: perm });
                toast.success("User created");
            }
            onSaved();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="max-w-md" data-testid="user-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display">
                        {editing ? "Edit user" : "Add new user"}
                    </DialogTitle>
                    <DialogDescription className="text-xs text-slate-500">
                        {editing ? "Update the user's details or reset their password." : "Create a new sub-user in your company. They will not be able to delete any record regardless of role."}
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <div>
                        <Label className="text-xs uppercase tracking-widest text-slate-500">Name *</Label>
                        <Input data-testid="user-form-name" value={name} onChange={(e) => setName(e.target.value)} className="mt-1" />
                    </div>
                    <div>
                        <Label className="text-xs uppercase tracking-widest text-slate-500">Email {editing ? "" : ""}</Label>
                        <Input
                            data-testid="user-form-email"
                            value={email}
                            type="email"
                            onChange={(e) => setEmail(e.target.value)}
                            className="mt-1"
                            placeholder="optional if mobile is provided"
                        />
                    </div>
                    <div>
                        <Label className="text-xs uppercase tracking-widest text-slate-500">Mobile</Label>
                        <Input
                            data-testid="user-form-phone"
                            value={phone}
                            type="tel"
                            inputMode="numeric"
                            maxLength={10}
                            onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
                            className="mt-1 font-mono"
                            placeholder="10-digit Indian mobile"
                        />
                        <div className="mt-1 text-[10px] text-slate-500">Provide email or mobile (or both). User can log in with whichever is set.</div>
                    </div>
                    <div>
                        <Label className="text-xs uppercase tracking-widest text-slate-500">
                            <Key size={11} weight="fill" className="mr-1 inline align-middle" />
                            {editing ? "New password (leave blank to keep)" : "Password *"}
                        </Label>
                        <Input
                            data-testid="user-form-password"
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="mt-1"
                            placeholder={editing ? "Leave blank to keep" : "min 6 characters"}
                        />
                    </div>
                    <div>
                        <Label className="text-xs uppercase tracking-widest text-slate-500">Role / Permission *</Label>
                        <Select value={perm} onValueChange={setPerm}>
                            <SelectTrigger data-testid="user-form-perm" className="mt-1">
                                <SelectValue placeholder="Choose access level" />
                            </SelectTrigger>
                            <SelectContent>
                                {Object.entries(PERMISSION_LABELS).map(([k, v]) => (
                                    <SelectItem key={k} value={k} data-testid={`user-form-perm-${k}`}>
                                        {v.label} — {v.desc}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="rounded-md border border-amber-200 bg-amber-50 p-2.5 text-[11px] text-amber-800">
                        Reminder: staff sub-users can <b>never delete</b> any record — this rule applies to every role including Full Access.
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
                    <Button data-testid="user-form-save" onClick={save} disabled={saving} className="bg-violet-600 text-white hover:bg-violet-700">
                        {saving ? "Saving…" : editing ? "Save changes" : "Create user"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
